import React, { forwardRef, MouseEvent, ReactElement } from 'react';

import PropTypes from 'prop-types';

import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

type Value = string;
type CreateNewMenuItemProps = StylableComponent<HTMLLIElement> & {
  value: Value;
  isHighlighted?: boolean;
  onCreate?: (value: Value, e: MouseEvent<HTMLButtonElement>) => void;
};

const CreateNewMenuItem = forwardRef<HTMLLIElement, CreateNewMenuItemProps>(
  (
    { className, style, value, isHighlighted, onCreate, ...props },
    ref,
  ): ReactElement => {
    const { T } = useLocale(({ locale }) =>
      require(`../../i18n/${locale}.json`),
    );
    return (
      <li
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            alignItems: 'flex-start',
            padding: `${theme.spacing.xSmall}px ${theme.spacing.medium}px`,
            cursor: 'pointer',
            ...(isHighlighted && { backgroundColor: theme.colors.charcoal050 }),
            '&:active': { backgroundColor: theme.colors.charcoal100 },
          },
          style,
        ]}
        {...props}
      >
        <LinkButton
          onClick={(e) => {
            onCreate?.(value, e);
          }}
        >
          <span css={[theme.typography.ellipsis]}>
            {T.translate('CREATE_OPTION', { label: value })}
          </span>
        </LinkButton>
      </li>
    );
  },
);

CreateNewMenuItem.displayName = 'CreateNewMenuItem';

CreateNewMenuItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
  onCreate: PropTypes.func,
};

export default CreateNewMenuItem;
